import { useMemo } from 'react'
import { Card, HealthGauge, TrendIndicator } from '@/components/common'
import { Activity, TrendingUp, AlertTriangle, Brain, Zap } from 'lucide-react'
import clsx from 'clsx'

interface FleetHealthScoreProps {
  score: number
  previousScore?: number
  loading?: boolean
  uptime?: number
  atRiskCount?: number
  criticalCount?: number
  warningCount?: number
  totalAssets?: number
}

export default function FleetHealthScore({
  score,
  previousScore,
  loading = false,
  uptime,
  atRiskCount,
  criticalCount = 0,
  warningCount = 0,
  totalAssets = 0
}: FleetHealthScoreProps) {
  const change = previousScore ? score - previousScore : 0
  const trend = change > 0 ? 'up' : change < 0 ? 'down' : 'stable'
  const trendValue = Math.abs(change)

  // Determine status message
  const getStatusMessage = () => {
    if (score >= 90) return 'Excellent'
    if (score >= 80) return 'Good'
    if (score >= 70) return 'Fair'
    if (score >= 60) return 'Needs Attention'
    return 'Critical'
  }

  const getStatusColor = () => {
    if (score >= 85) return 'text-status-healthy'
    if (score >= 60) return 'text-status-warning'
    return 'text-status-critical'
  }

  // Generate AI insight based on fleet status
  const aiInsight = useMemo(() => {
    const healthyCount = totalAssets - criticalCount - warningCount
    const healthyPercent = totalAssets > 0 ? Math.round((healthyCount / totalAssets) * 100) : 0

    if (criticalCount > 0 && score < 60) {
      return {
        text: `${criticalCount} critical asset${criticalCount > 1 ? 's require' : ' requires'} immediate attention. AI models predict potential cascade risk if unaddressed.`,
        priority: 'critical' as const,
        action: 'Review critical alerts immediately'
      }
    }
    if (criticalCount > 0) {
      return {
        text: `${criticalCount} critical asset${criticalCount > 1 ? 's' : ''} detected. Proactive maintenance recommended within 24-48 hours to prevent unplanned downtime.`,
        priority: 'high' as const,
        action: 'Schedule maintenance'
      }
    }
    if (warningCount > 2) {
      return {
        text: `${warningCount} assets showing early warning signs. Pattern analysis suggests shared environmental or operational factors.`,
        priority: 'medium' as const,
        action: 'Investigate patterns'
      }
    }
    if (score >= 90) {
      return {
        text: `Fleet operating at peak efficiency. ${healthyPercent}% of assets in optimal condition. Continue current maintenance cadence.`,
        priority: 'good' as const,
        action: null
      }
    }
    if (score >= 80) {
      return {
        text: `Fleet health stable. ${warningCount > 0 ? `${warningCount} asset${warningCount > 1 ? 's' : ''} trending toward warning threshold.` : 'All assets within normal parameters.'}`,
        priority: 'good' as const,
        action: warningCount > 0 ? 'Monitor trends' : null
      }
    }
    return {
      text: `Fleet health below target. AI recommends reviewing maintenance schedule to improve overall reliability.`,
      priority: 'medium' as const,
      action: 'Review schedule'
    }
  }, [score, criticalCount, warningCount, totalAssets])

  if (loading) {
    return (
      <Card className="h-full">
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-24 h-24 rounded-full bg-surface-700 animate-pulse" />
          <div className="w-20 h-4 bg-surface-700 rounded mt-4 animate-pulse" />
        </div>
      </Card>
    )
  }

  return (
    <Card className="h-full">
      <div className="flex flex-col items-center">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <Activity className="text-primary-400" size={20} />
          <span className="text-sm font-medium text-surface-300">Fleet Health</span>
        </div>

        {/* Gauge */}
        <HealthGauge value={score} size="lg" showLabel={false} />

        {/* Status */}
        <div className="mt-4 text-center">
          <span className={clsx('text-lg font-semibold', getStatusColor())}>
            {getStatusMessage()}
          </span>
        </div>

        {/* Trend */}
        {previousScore && (
          <div className="mt-3 flex items-center gap-2">
            <TrendIndicator
              direction={trend}
              value={trendValue}
              variant="detailed"
              label="vs last period"
            />
          </div>
        )}

        {/* Quick Stats */}
        <div className="mt-6 w-full grid grid-cols-2 gap-3">
          <div className="bg-surface-900 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1 text-status-healthy mb-1">
              <TrendingUp size={14} />
              <span className="text-xs font-medium">Uptime</span>
            </div>
            <span className="text-lg font-mono font-semibold text-surface-200">
              {uptime !== undefined ? `${uptime.toFixed(1)}%` : '--'}
            </span>
          </div>
          <div className="bg-surface-900 rounded-lg p-3 text-center">
            <div className={clsx(
              'flex items-center justify-center gap-1 mb-1',
              atRiskCount && atRiskCount > 0 ? 'text-status-warning' : 'text-status-healthy'
            )}>
              <AlertTriangle size={14} />
              <span className="text-xs font-medium">At Risk</span>
            </div>
            <span className="text-lg font-mono font-semibold text-surface-200">
              {atRiskCount !== undefined ? atRiskCount : '--'}
            </span>
          </div>
        </div>

        {/* AI Insight */}
        <div className={clsx(
          'mt-4 w-full p-3 rounded-lg border',
          aiInsight.priority === 'critical' && 'bg-red-500/10 border-red-500/30',
          aiInsight.priority === 'high' && 'bg-amber-500/10 border-amber-500/30',
          aiInsight.priority === 'medium' && 'bg-yellow-500/10 border-yellow-500/30',
          aiInsight.priority === 'good' && 'bg-emerald-500/10 border-emerald-500/30'
        )}>
          <div className="flex items-start gap-2">
            <Brain size={14} className={clsx(
              'mt-0.5 flex-shrink-0',
              aiInsight.priority === 'critical' && 'text-red-400',
              aiInsight.priority === 'high' && 'text-amber-400',
              aiInsight.priority === 'medium' && 'text-yellow-400',
              aiInsight.priority === 'good' && 'text-emerald-400'
            )} />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-surface-300 leading-relaxed">
                {aiInsight.text}
              </p>
              {aiInsight.action && (
                <div className="flex items-center gap-1 mt-2">
                  <Zap size={10} className="text-primary-400" />
                  <span className="text-[10px] font-medium text-primary-400 uppercase tracking-wider">
                    {aiInsight.action}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}
